import { useEffect, useId, useRef } from 'react';

import { PWA_COPY } from '../../utils/pwaInstall';

/**
 * Accessible iOS / iPadOS manual install instructions dialog.
 * Opened only after an explicit user action.
 */
export default function PwaInstallInstructions({
  open,
  onClose,
  returnFocusRef,
}) {
  const dialogRef = useRef(null);
  const closeBtnRef = useRef(null);
  const titleId = useId();
  const descId = useId();

  useEffect(() => {
    if (!open) return undefined;

    const previouslyFocused = document.activeElement;
    const returnTarget = returnFocusRef && returnFocusRef.current
      ? returnFocusRef.current
      : previouslyFocused;

    const focusTimer = window.setTimeout(() => {
      if (closeBtnRef.current) {
        closeBtnRef.current.focus();
      } else if (dialogRef.current) {
        dialogRef.current.focus();
      }
    }, 0);

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    };

    document.addEventListener('keydown', onKeyDown);

    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener('keydown', onKeyDown);
      if (returnTarget && typeof returnTarget.focus === 'function') {
        returnTarget.focus();
      }
    };
  }, [open, onClose, returnFocusRef]);

  if (!open) return null;

  return (
    <div className="pwa-install-instructions" data-testid="pwa-install-instructions">
      <button
        type="button"
        className="pwa-install-instructions__backdrop"
        aria-label={PWA_COPY.close}
        onClick={onClose}
      />
      <div
        ref={dialogRef}
        className="pwa-install-instructions__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
        tabIndex={-1}
      >
        <div className="pwa-install-instructions__header">
          <h2 id={titleId} className="pwa-install-instructions__title">
            {PWA_COPY.instructionsTitle}
          </h2>
          <button
            ref={closeBtnRef}
            type="button"
            className="pwa-install-instructions__close"
            aria-label={PWA_COPY.close}
            onClick={onClose}
          >
            <span aria-hidden="true">×</span>
          </button>
        </div>
        <ol id={descId} className="pwa-install-instructions__steps">
          {PWA_COPY.instructions.map((step) => (
            <li key={step}>{step}</li>
          ))}
        </ol>
        <div className="pwa-install-instructions__actions">
          <button
            type="button"
            className="action-btn action-btn--accent pwa-install-instructions__done"
            onClick={onClose}
          >
            {PWA_COPY.close}
          </button>
        </div>
      </div>
    </div>
  );
}
